import { Box, Divider } from '@mui/material';
import React, { useEffect, useCallback, useMemo } from 'react';
import { Controller, FieldValues, Path, PathValue, useFormContext, useWatch } from 'react-hook-form';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import PathControlledDropdown, {
  ControlledDropdown
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledDropdown';
import PathControlledCheckbox from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledCheckbox';
import {
  AtlasFilterFieldTypeSchema,
  MetricAdjustmentsSchema,
  BeaconFilterFieldTypeSchema,
  FilterOperatorSchema,
  FilterTypeToConceptMap
} from '../schema/AlertBuilderSchema';
import { BeaconFilterFieldTypeType, FilterConfigType } from '../schema/AlertBuilderSchemaTypes';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import PathControlledNumberInput from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledNumberInput';
import CloseIconButton from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/CloseIconButton';
import { FilterArrayValuesAutocomplete } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/FilterValuesAutocomplete';
import {
  useFormBrands,
  useFormBusinessUnits,
  useFormCampaigns,
  useFormCategories,
  useFormRetailers,
  useFormSegments,
  useFormSubcategories
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/hooks/useCategoricalFilters';
import RuleButton from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/RuleButton';
import FilterChip from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/FilterChip';
import { DROPDOWN_WIDTH, isAddDisabledByTypePool } from '../utils';
import { getAppName } from 'src/utils/app';

type Props<T extends FieldValues> = {
  groupIndex: number;
  blockIndex: number;
};

function MetricAdjustmentSection<T extends FieldValues>({ groupIndex, blockIndex }: Props<T>) {
  // Ensure form context is available for path-based components
  const { setValue, control, watch } = useFormContext<T>();
  const appName = getAppName();
  const basePath = `queryJSON.rules.ruleGroups.${groupIndex}.ruleBlocks.${blockIndex}.metricAdjustment` as Path<T>;
  const useReferenceFilter = useWatch({ name: `${basePath}.useReferenceFilter` as Path<T> });
  const showAdjustmentValue = useReferenceFilter === false || useReferenceFilter === undefined;
  const referenceFiltersPath = `${basePath}.referenceFilters` as Path<T>;
  const watchedReferenceFilters = useWatch({ name: referenceFiltersPath });
  const referenceFilters = useMemo(() => {
    return (watchedReferenceFilters as FilterConfigType[]) || [];
  }, [watchedReferenceFilters]);
  const metricName = useWatch({
    name: `queryJSON.rules.ruleGroups.${groupIndex}.ruleBlocks.${blockIndex}.metric` as Path<T>
  });
  const formCategories = useFormCategories();
  const formSubcategories = useFormSubcategories();
  const formRetailers = useFormRetailers();
  const formBrands = useFormBrands();
  const formSegments = useFormSegments();
  const formBusinessUnits = useFormBusinessUnits();
  const formCampaigns = useFormCampaigns();

  const addReferenceFilter = useCallback(() => {
    const next = [
      ...referenceFilters,
      {
        type: '',
        operator: 'IN',
        value: '',
        values: []
      }
    ];
    setValue(referenceFiltersPath, next as PathValue<T, typeof referenceFiltersPath>);
  }, [referenceFilters, setValue, referenceFiltersPath]);

  // Automatically add a blank filter when "Use Reference Metric" is checked
  useEffect(() => {
    if (useReferenceFilter === true && referenceFilters.length === 0) {
      addReferenceFilter();
    }
  }, [useReferenceFilter, referenceFilters.length, addReferenceFilter]);

  const isNumericType = (type: BeaconFilterFieldTypeType): boolean =>
    ['Category', 'Subcategory', 'Brand', 'Retailer'].includes(type);

  const getItemsForType = (
    categoricalType: BeaconFilterFieldTypeType
  ): Array<{ id: string; name: string }> => {
    switch (categoricalType) {
      case 'Brand':
        return formBrands;
      case 'Category':
        return formCategories;
      case 'Subcategory':
        return formSubcategories;
      case 'Retailer':
        return formRetailers;
      case 'Business Unit':
        return formBusinessUnits;
      case 'Segment':
        return formSegments;
      case 'Campaign':
        return formCampaigns;
      case 'RetailerSku':
        // Intentionally empty to allow freeSolo entries at scale
        return [];
      case 'Keyword':
        return [];
      default:
        return [];
    }
  };

  const buildItemForType = (type: BeaconFilterFieldTypeType, label: string): { id: string; name: string } => {
    const items = getItemsForType(type);
    const found = items.find((i) => i.name === label);
    if (found) return found;
    if (!isNumericType(type)) return { id: label, name: label };
    return items[0] ?? { id: '0', name: label };
  };

  const getOptionsForFilter = (filter: FilterConfigType): string[] => {
    return getItemsForType(filter.type).map((i) => i.name);
  };

  // Prevent duplicate types across reference filter rows; keep current row's selection available
  const getAvailableReferenceTypeOptions = (rowIndex: number): string[] => {
    const allOptions = appName === 'atlas' ? AtlasFilterFieldTypeSchema.options : BeaconFilterFieldTypeSchema.options;
    const selected = new Set<string>(referenceFilters.map((f) => f.type).filter(Boolean) as string[]);
    const currentType = referenceFilters[rowIndex]?.type as string | undefined;
    if (currentType) selected.delete(currentType);
    const referenceTypes = allOptions.filter((t) => !selected.has(t));
    return referenceTypes;
  };

  const removeReferenceFilter = (index: number) => {
    const next = referenceFilters.filter((_: FilterConfigType, i: number) => i !== index);
    setValue(referenceFiltersPath, next as PathValue<T, typeof referenceFiltersPath>);
  };

  return (
    <Box sx={{ marginTop: '16px' }}>
      <Divider sx={{ marginBottom: '16px', borderColor: colors.graysSecondary }} />

      <Text variant="body1" sx={{ fontWeight: 600, marginBottom: '16px', display: 'block' }}>
        Metric Adjustment
      </Text>

      {/* First row: components with absolute Close icon at far right */}
      <Box sx={{ position: 'relative', marginBottom: '16px', paddingRight: '40px' }}>
        <Flex gap={16} alignItems="center">
          <Box sx={{ width: DROPDOWN_WIDTH }}>
            <PathControlledDropdown
              name={`${basePath}.operation` as Path<T>}
              label=""
              options={MetricAdjustmentsSchema.options}
              placeholder="Select Adjustment Type"
            />
          </Box>
          {showAdjustmentValue && (
            <Box sx={{ maxWidth: '100px' }}>
              <PathControlledNumberInput
                name={`${basePath}.adjustmentValue` as Path<T>}
                label=""
                min={1}
                max={999999}
              />
            </Box>
          )}
          <Box sx={{ marginTop: '4px' }}>
            <PathControlledCheckbox
              name={`${basePath}.useReferenceFilter` as Path<T>}
              label="Use Reference Metric"
              sx={{
                '&.Mui-checked': {
                  color: colors.darkBlue
                },
                '&.Mui-checked:hover': {
                  backgroundColor: `${colors.darkBlue}14` // 8% opacity
                }
              }}
            />
          </Box>
        </Flex>

        {/* Remove this metric adjustment button */}
        <Box sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
          <CloseIconButton
            onClick={() => (setValue as (p: Path<T>, v: PathValue<T, typeof basePath>) => void)(basePath, undefined)}
          />
        </Box>
      </Box>

      {/* Reference metric filters section (visible only when using reference filter) */}
      {useReferenceFilter === true && (
        <>
          <Flex sx={{ marginBottom: '16px' }} flexDirection="row" gap={6} alignItems="center">
            <FilterChip keyProp={`metric-${metricName}`} label={metricName || ''} />
            <Text variant="body2">where</Text>
          </Flex>
          <Box sx={{ marginTop: '8px' }}>
            {referenceFilters.map((filter: FilterConfigType, index: number) => (
              <Flex key={index ?? `rf-${index}`} gap={16} alignItems="center" sx={{ marginBottom: '12px' }}>
                {/* Type */}
                <Box sx={{ flex: 1, maxWidth: '200px' }}>
                  <Controller
                    name={`${referenceFiltersPath}.${index}.type` as Path<T>}
                    control={control}
                    render={({ field: { value }, fieldState: { error } }) => (
                      <ControlledDropdown
                        label=""
                        value={value || ''}
                        onChange={(newValue) => {
                          const typePath = `${referenceFiltersPath}.${index}.type` as Path<T>;
                          const conceptPath = `${referenceFiltersPath}.${index}.concept` as Path<T>;
                          const valuePath = `${referenceFiltersPath}.${index}.value` as Path<T>;
                          const valuesPath = `${referenceFiltersPath}.${index}.values` as Path<T>;
                          setValue(typePath, newValue as PathValue<T, typeof typePath>);
                          const concept = (FilterTypeToConceptMap as Record<string, string>)[newValue as string];
                          if (concept) setValue(conceptPath, concept as PathValue<T, typeof conceptPath>);
                          setValue(valuePath, null as PathValue<T, typeof valuePath>);
                          setValue(valuesPath, [] as PathValue<T, typeof valuesPath>);
                        }}
                        options={getAvailableReferenceTypeOptions(index)}
                        placeholder="Select Filter Type"
                        error={!!error}
                      />
                    )}
                  />
                </Box>

                {/* Operator */}
                <Box sx={{ flex: 1, maxWidth: '60px' }}>
                  <Controller
                    name={`${referenceFiltersPath}.${index}.operator` as Path<T>}
                    control={control}
                    render={({ field: { value }, fieldState: { error } }) => (
                      <ControlledDropdown
                        label=""
                        value={value || FilterOperatorSchema.enum['IN']}
                        onChange={(newValue) => {
                          const operatorPath = `${referenceFiltersPath}.${index}.operator` as Path<T>;
                          const valuePath = `${referenceFiltersPath}.${index}.value` as Path<T>;
                          const valuesPath = `${referenceFiltersPath}.${index}.values` as Path<T>;
                          setValue(operatorPath, newValue as PathValue<T, typeof operatorPath>);
                          setValue(valuePath, null as PathValue<T, typeof valuePath>);
                          setValue(valuesPath, [] as PathValue<T, typeof valuesPath>);
                        }}
                        options={FilterOperatorSchema.options}
                        placeholder={FilterOperatorSchema.enum['IN']}
                        error={!!error}
                      />
                    )}
                  />
                </Box>

                {/* Values */}
                <FilterArrayValuesAutocomplete
                  idBase={`${index}-${filter.type}`}
                  options={getOptionsForFilter(filter)}
                  freeSolo={getOptionsForFilter(filter).length === 0}
                  placeholder={getOptionsForFilter(filter).length === 0 ? 'Enter to add value' : undefined}
                  name={`${referenceFiltersPath}.${index}.values` as Path<T>}
                  buildItem={(label) => buildItemForType(filter.type as BeaconFilterFieldTypeType, label)}
                  fieldType={filter.type}
                />

                {/* Remove */}
                <Box sx={{ flex: 0, marginTop: '5px' }}>
                  <CloseIconButton onClick={() => removeReferenceFilter(index)} />
                </Box>
              </Flex>
            ))}

            <Flex gap={16} style={{ marginTop: '8px' }}>
              <RuleButton
                onClick={addReferenceFilter}
                disabled={isAddDisabledByTypePool(
                  referenceFilters as Array<{ type?: string }>,
                  BeaconFilterFieldTypeSchema.options
                )}
              >
                + Add Reference Metric Categorical Filter
              </RuleButton>
            </Flex>
          </Box>
        </>
      )}
    </Box>
  );
}

export default MetricAdjustmentSection;
